<template>
  <div data-cy="ds-file-upload-wrapper">
    <DSLabel
      v-if="props.label"
      :name="id"
      :label="props.label"
      :tooltip="props.tooltip"
      :hidden-label="props.hiddenLabel"
      :required="props.required"
      :has-error="hasError"
      :disabled="props.disabled"
    />

    <div
      ref="rootRef"
      :class="rootClasses"
      data-cy="ds-file-upload"
      :aria-disabled="props.disabled || undefined"
      :aria-invalid="hasError ? 'true' : undefined"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      @focus.capture="onFocus"
      @blur.capture="onBlur"
    >
      <DSFileUploadDropzone
        :id="id"
        ref="dropzoneRef"
        :accept-attribute="acceptAttribute"
        :multiple="props.multiple"
        :disabled="props.disabled"
        :required="props.required && modelValue.length === 0"
        :max-file-size="props.maxFileSize"
        :description="props.description"
        @files-selected="onFilesSelected"
        @focus="isDropzoneFocused = true"
        @blur="isDropzoneFocused = false"
      />

      <DSFileUploadNativeFileField
        v-for="item in modelValue"
        :key="`native-${item.id}`"
        :item="item"
        :disabled="props.disabled"
        :file-field-name="fileFieldName(item)"
        :filename-field-name="filenameFieldName(item)"
      />

      <DSProgressBar
        v-if="showProgressBar"
        :class="DS_FILE_UPLOAD_CLASSES.Progress"
        :value="props.progress!"
      />

      <DSFileUploadErrorList
        :error-messages="dsFormField.errorMessages.value"
        :error-key-prefix="props.name || id"
        data-cy="ds-file-upload__error-list"
      />

      <DSFileUploadFileList
        v-if="showFiles"
        :items="modelValue"
        :disabled="props.disabled"
        @remove="removeFile"
        @remove-all="removeAllFiles"
      />
    </div>

    <Teleport to="body">
      <div
        v-if="isPageDragActive"
        :class="DS_FILE_UPLOAD_CLASSES.PageDropOverlay"
        data-cy="ds-file-upload__page-drop-overlay"
      >
        <div
          :class="[
            DS_FILE_UPLOAD_CLASSES.Root,
            DS_FILE_UPLOAD_CLASSES.Default,
            DS_FILE_UPLOAD_CLASSES.DragActive,
            DS_FILE_UPLOAD_CLASSES.PageDropzone,
          ]"
          data-cy="ds-file-upload__page-dropzone"
        >
          <DSFileUploadDropzone
            :id="`${id}-page-dropzone`"
            :accept-attribute="acceptAttribute"
            :multiple="props.multiple"
            :disabled="false"
            :required="false"
            :max-file-size="props.maxFileSize"
            :description="props.description"
            @files-selected="onFilesSelected"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import DSFileUploadDropzone from "./DSFileUploadDropzone.vue";
import DSFileUploadErrorList from "./DSFileUploadErrorList.vue";
import DSFileUploadFileList from "./DSFileUploadFileList.vue";
import DSFileUploadNativeFileField from "./DSFileUploadNativeFileField.vue";
import DSLabel from "@/src/design_system/form_elements/label/components/DSLabel.vue";
import DSProgressBar from "@/src/design_system/feedback/progress_bar/components/DSProgressBar.vue";
import { useDSFileUpload } from "../composables/useDSFileUpload";
import { DS_FILE_UPLOAD_CLASSES } from "../constants/classes";
import type { DSFileUploadComponentProps, DSFileUploadEmits } from "../types";

const props = withDefaults(defineProps<DSFileUploadComponentProps>(), {
  hiddenLabel: false,
  modelValue: () => [],
  required: false,
  disabled: false,
  errorMessages: undefined,
  rules: undefined,
  accept: undefined,
  multiple: true,
  maxFileSize: undefined,
  description: undefined,
  maxFiles: undefined,
  fullPageDropzone: false,
  showFileList: true,
  progress: undefined,
  tooltip: undefined,
});

const emit = defineEmits<DSFileUploadEmits>();
const rootRef = ref<HTMLElement>();

const {
  acceptAttribute,
  blur,
  dsFormField,
  fileFieldName,
  filenameFieldName,
  focus,
  hasError,
  id,
  isDropzoneFocused,
  isPageDragActive,
  dropzoneRef,
  modelValue,
  onBlur,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onFilesSelected,
  onFocus,
  openFileDialog,
  removeAllFiles,
  removeFile,
  resetInput,
  rootClasses,
  showFiles,
  showProgressBar,
} = useDSFileUpload({ props, emit, rootRef });

defineExpose({
  focus,
  blur,
  openFileDialog,
  resetInput,
});
</script>
